import { SelectItem } from '@/components/ui/select';
import { SelectField } from './SelectField';
import { useNode } from '@craftjs/core';
import { useFonts } from '@/context/FontProvider';
import { cn } from '@/lib/utils';
import type { CountdownProps } from '@builder4/shared/components/Countdown';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';

const NodeCountdownFontFamily = ({
  onSelect,
  className = 'max-w-[216px]',
  type,
}: {
  onSelect?: (fontFamily: string) => void;
  className?: string;
  type: 'label' | 'number';
}) => {
  const { fonts } = useFonts();
  const {
    props,
    actions: { setProp },
    displayName,
  } = useNode((node) => ({
    node: node,
    id: node.id,
    props: node.data.props,
    displayName: node.data.displayName,
  }));

  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);

  const labelFontFamily = getBPProp(props, 'labelFontFamily');
  const numberFontFamily = getBPProp(props, 'numberFontFamily');

  const handleFontChange = (family: string) => {
    onSelect?.(family);
    if (type === 'label') {
      setProp((props: CountdownProps) => {
        setBPProp(props, 'labelFontFamily', family);
        setBPProp(props, 'labelFontWeight', '400');
      });
      return;
    } else {
      setProp((props: CountdownProps) => {
        setBPProp(props, 'numberFontFamily', family);
        setBPProp(props, 'numberFontWeight', '400');
      });
    }
  };

  const getFontFamily = () => {
    if (type === 'label') {
      return labelFontFamily;
    } else {
      return numberFontFamily;
    }
  };

  return (
    <>
      <SelectField
        onValueChange={handleFontChange}
        className={cn(className)}
        defaultValue={getFontFamily()}
        size="small"
        text="Font"
        dataTestId="font-selection"
      >
        {fonts.length > 0 ? (
          fonts.map((font) => (
            <SelectItem
              key={font.family}
              value={font.family}
              style={{
                fontFamily: font.family,
                fontSize: '14px',
                fontWeight: '400',
              }}
            >
              {font.family}
            </SelectItem>
          ))
        ) : (
          <SelectItem value="loading" disabled>
            Loading fonts...
          </SelectItem>
        )}
      </SelectField>
    </>
  );
};

export default NodeCountdownFontFamily;
